import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLivePoll } from '../hooks/useLivePoll';
import { useLiveParticipantId } from '../hooks/useLiveParticipantId';
import { getSession, joinSession, submitAnswer } from '../utils/liveApi';
import ParticipantQuestionCard from '../components/live/ParticipantQuestionCard';
import questionsData from '../data/orientation-axis-questions.json';

const POLL_INTERVAL_MS = 1500;

export default function LiveParticipantPage() {
  const { code: rawCode } = useParams();
  const code = (rawCode || '').toUpperCase();
  const participantId = useLiveParticipantId(code);
  const navigate = useNavigate();

  const [joinState, setJoinState] = useState({ loading: true, error: null, displayLabel: null });
  const [myAnswers, setMyAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Register this participant (or reattach if their participantId already exists).
  useEffect(() => {
    if (!code || !participantId) return undefined;
    let alive = true;
    setJoinState({ loading: true, error: null, displayLabel: null });
    joinSession({ code, participantId })
      .then((r) => {
        if (!alive) return;
        setJoinState({ loading: false, error: null, displayLabel: r.displayLabel });
        setMyAnswers(r.myAnswers || {});
      })
      .catch((e) => {
        if (!alive) return;
        setJoinState({ loading: false, error: e, displayLabel: null });
      });
    return () => { alive = false; };
  }, [code, participantId]);

  const pollEnabled = !joinState.loading && !joinState.error && !!participantId;

  const fetcher = useCallback(
    ({ knownVersion, signal }) => getSession({ code, participantId, knownVersion, signal }),
    [code, participantId]
  );

  const { data } = useLivePoll(fetcher, POLL_INTERVAL_MS, { enabled: pollEnabled });

  // Keep myAnswers in sync with the server snapshot when version changes
  // (e.g. when the animator advances between questions).
  useEffect(() => {
    if (data && data.myAnswers) {
      setMyAnswers(data.myAnswers);
    }
  }, [data]);

  // Auto-navigate to the result page once the animator ends the session.
  useEffect(() => {
    if (data && data.state === 'ended') {
      navigate(`/live/${code}/result`, { replace: true });
    }
  }, [data, code, navigate]);

  const currentQuestion = useMemo(() => {
    if (!data) return null;
    if (data.state !== 'question' && data.state !== 'revealed') return null;
    return questionsData.questions[data.currentQuestionIndex];
  }, [data]);

  const totalQuestions = data?.totalQuestions || questionsData.questions.length;
  const currentSelected =
    data && (data.state === 'question' || data.state === 'revealed')
      ? myAnswers[String(data.currentQuestionIndex)]
      : null;
  const locked = currentSelected !== undefined && currentSelected !== null;

  const handleSelect = useCallback(
    async (optionIndex) => {
      if (!data) return;
      if (data.state !== 'question') return;
      const qIdx = data.currentQuestionIndex;
      setSubmitting(true);
      setSubmitError(null);
      try {
        await submitAnswer({ code, participantId, questionIndex: qIdx, optionIndex });
        setMyAnswers((prev) => ({ ...prev, [String(qIdx)]: optionIndex }));
      } catch (e) {
        const err = e.response?.data?.error;
        if (err === 'answer_already_locked') {
          // Server says we already answered — reconcile by re-reading.
          // Local optimistic state will catch up on next non-unchanged poll.
        } else if (err === 'wrong_question' || err === 'not_accepting_answers') {
          setSubmitError("La question a déjà changé.");
        } else {
          setSubmitError(err || e.message || 'Erreur inconnue');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [code, participantId, data]
  );

  if (joinState.loading) {
    return (
      <div className="container" style={{ marginTop: '2rem', textAlign: 'center' }}>
        <div className="loading">Connexion à la session…</div>
      </div>
    );
  }
  if (joinState.error) {
    const status = joinState.error?.response?.status;
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <h2>Impossible de rejoindre</h2>
        {status === 404 && <p>Aucune session active ne correspond au code <code>{code}</code>.</p>}
        {status === 410 && <p>Cette session est déjà terminée.</p>}
        {!status && <p>{joinState.error.message || 'Erreur réseau.'}</p>}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container" style={{ marginTop: '2rem', textAlign: 'center' }}>
        <div className="loading">Chargement…</div>
      </div>
    );
  }

  return (
    <div
      className="container"
      style={{
        marginTop: '1rem',
        maxWidth: 560,
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong style={{ color: '#7f8c8d', fontSize: '0.85rem', letterSpacing: '0.1em' }}>
          SESSION {code}
        </strong>
        <span style={{ color: '#bdc3c7', fontSize: '0.8rem' }}>{joinState.displayLabel}</span>
      </div>

      {data.state === 'lobby' && (
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
          <h3 style={{ margin: 0 }}>En attente du démarrage</h3>
          <p style={{ color: '#7f8c8d' }}>L'animateur lancera la première question dans un instant.</p>
        </div>
      )}

      {currentQuestion && (
        <>
          <ParticipantQuestionCard
            question={currentQuestion}
            questionNumber={data.currentQuestionIndex + 1}
            totalQuestions={totalQuestions}
            selectedOptionIndex={currentSelected}
            locked={locked || data.state === 'revealed'}
            submitting={submitting}
            onSelect={handleSelect}
          />
          {submitError && (
            <div style={{ padding: '0.75rem 1rem', background: '#fdecea', color: '#c0392b', borderRadius: 6 }}>
              {submitError}
            </div>
          )}
          {data.state === 'revealed' && (
            <div style={{ textAlign: 'center', color: '#7f8c8d', fontSize: '0.9rem' }}>
              Le groupe regarde les résultats de cette question…
            </div>
          )}
        </>
      )}

      <div style={{ textAlign: 'center', color: '#bdc3c7', fontSize: '0.75rem' }}>
        Anonyme. Ne fermez pas cet onglet — votre identité de session serait perdue.
      </div>
    </div>
  );
}
